/**
 * Session Capture - Protocol Import view (Sprint 3.4 REDO).
 *
 * Three-column protocol-import workflow per the canonical prototype at
 * `https://ai-native-prototype.vercel.app/care-pathway-studio.html`:
 *
 *   - Left rail: ImportSourcePanel (Scrips PROM Engine summary)
 *   - Center: InstrumentMultiselect + AdminScheduleSelector +
 *             FhirOutputPreview
 *   - Right rail: PlanDefinitionMappingPanel ("Maps to TLP1 FHIR
 *             PlanDefinition")
 *   - Bottom: Publish CTA writing the FHIR `PlanDefinition` and
 *             emitting an AuditEvent with `source =
 *             session-capture-publish`.
 *
 * NOT WebSpeech voice capture; NOT document upload. The Sprint 3.4
 * REDO supervisor brief replaces the stopped WebSpeech dispatch with
 * this protocol-import workflow.
 *
 * HC1 reproducibility: every render is deterministic from the
 *   selection + schedule state.
 * HC2 token-only.
 * HC5 zero em / en dashes.
 * HC8 fail-closed publish: refuses with no instruments selected.
 * HC9 strict registry: AuditEvent always carries source.
 */
import { useState } from 'react';
import { ImportSourcePanel } from '../components/session-capture/ImportSourcePanel';
import { InstrumentMultiselect } from '../components/session-capture/InstrumentMultiselect';
import { AdminScheduleSelector } from '../components/session-capture/AdminScheduleSelector';
import { FhirOutputPreview } from '../components/session-capture/FhirOutputPreview';
import { PlanDefinitionMappingPanel } from '../components/session-capture/PlanDefinitionMappingPanel';
import {
  emptyScheduleAssignments,
  type PromInstrumentCode,
  type ScheduleAssignments,
} from '../components/session-capture/types';
import { publishSessionCapture } from '../components/session-capture/publish';
import { getFhirClient } from '../services/fhir';
import { useTenantStore } from '../state/tenant.store';

interface PublishToast {
  kind: 'success' | 'error';
  message: string;
  planDefinitionId?: string;
}

export function SessionCapture() {
  const tenantId = useTenantStore((s) => s.tenantId);
  const [selected, setSelected] = useState<PromInstrumentCode[]>([]);
  const [assignments, setAssignments] = useState<ScheduleAssignments>(
    () => emptyScheduleAssignments(),
  );
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<PublishToast | null>(null);

  const canPublish = selected.length > 0 && !publishing;

  async function handlePublish() {
    if (!canPublish) return;
    setPublishing(true);
    setToast(null);
    try {
      const result = await publishSessionCapture(getFhirClient(), {
        selectedInstruments: selected,
        assignments,
        tenantId,
      });
      setToast({
        kind: 'success',
        message: 'Published. Open in Pathway Studio.',
        planDefinitionId: result.planDefinition.id,
      });
    } catch (err) {
      // HC8 + Sprint 2.2 hardening: surface the error inline; do NOT
      // swallow. TenantMismatchError, FhirValidationError, transport
      // failures all funnel here.
      const message =
        err instanceof Error ? err.message : 'Publish failed for unknown reason';
      setToast({ kind: 'error', message });
    } finally {
      setPublishing(false);
    }
  }

  return (
    <section
      aria-labelledby="view-title-cap"
      data-testid="session-capture-view"
      className="px-gutter-lg py-gutter-xl"
    >
      <header className="mb-gutter-xl">
        <h2
          id="view-title-cap"
          className="text-signal-2xl font-semibold text-text-primary"
        >
          Session Capture
        </h2>
        <p className="mt-gutter-md max-w-3xl text-signal-base text-text-secondary">
          Protocol Import. Ingest a PROM-engine selection and emit a FHIR
          PlanDefinition for the Cardiometabolic Pathway.
        </p>
      </header>
      <div
        data-testid="session-capture-grid"
        className="grid gap-gutter-lg lg:grid-cols-[minmax(220px,1fr)_minmax(0,2fr)_minmax(220px,1fr)]"
      >
        <ImportSourcePanel />
        <div className="flex flex-col gap-gutter-lg">
          <InstrumentMultiselect
            selected={selected}
            onChange={(next) => {
              setSelected(next);
              // Drop any schedule assignments that point at instruments
              // no longer selected so the schedule never references a
              // ghost instrument.
              setAssignments((prev) => {
                const filtered: ScheduleAssignments = {
                  ...prev,
                };
                (Object.keys(filtered) as Array<keyof ScheduleAssignments>).forEach(
                  (stepId) => {
                    filtered[stepId] = filtered[stepId].filter((c) =>
                      next.includes(c),
                    );
                  },
                );
                return filtered;
              });
            }}
          />
          <AdminScheduleSelector
            selectedInstruments={selected}
            assignments={assignments}
            onChange={setAssignments}
          />
          <FhirOutputPreview
            selectedInstruments={selected}
            assignments={assignments}
            tenantId={tenantId}
          />
        </div>
        <PlanDefinitionMappingPanel />
      </div>
      <footer className="mt-gutter-xl flex flex-col items-stretch gap-gutter-md sm:flex-row sm:items-center sm:justify-between">
        <p
          data-testid="session-capture-publish-helper"
          className="text-signal-xs text-text-tertiary"
        >
          {selected.length === 0
            ? 'Select at least one instrument before publishing.'
            : `${selected.length} instrument(s) ready to publish.`}
        </p>
        <button
          type="button"
          data-testid="session-capture-publish-cta"
          disabled={!canPublish}
          onClick={handlePublish}
          className="rounded-signal-md bg-status-active px-gutter-lg py-gutter-md text-signal-sm font-semibold text-text-inverse shadow-signal-card hover:bg-status-active-fg disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-text-tertiary"
        >
          {publishing ? 'Publishing...' : 'Publish Cardiometabolic Pathway to Pathway Studio'}
        </button>
      </footer>
      {toast && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          data-testid={`session-capture-toast-${toast.kind}`}
          className={
            toast.kind === 'error'
              ? 'mt-gutter-md rounded-signal-md border border-warning-strong-border bg-warning-strong-bg p-gutter-md text-signal-sm text-warning-strong-fg'
              : 'mt-gutter-md rounded-signal-md border border-status-active bg-status-active-bg p-gutter-md text-signal-sm text-status-active-fg'
          }
        >
          {toast.kind === 'success' ? (
            <span>
              {toast.message}{' '}
              <a
                data-testid="session-capture-toast-link"
                href="/"
                className="font-semibold underline"
              >
                Open in Pathway Studio
              </a>
            </span>
          ) : (
            <span>{toast.message}</span>
          )}
        </div>
      )}
    </section>
  );
}
